
import { Alert, Box, Button, CircularProgress, FormControl, Grid, InputLabel, List, ListItem, ListItemButton, ListItemText, ListSubheader, MenuItem, Modal, Popover, Select, Snackbar, Stack, Tab, Tabs, Typography, styled } from "@mui/material";
import { DownDetectorItem } from "./DownDetectorItem";
import { DownDetectorTable } from "./DownDetectorTable";
import { DownDetectorTitle } from "./DownDetectorTitle";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useFetchData } from "../../hooks/useFetchData";
import { useEffect, useState } from "react";
import { useFileContent } from "../../hooks/useFileContent";
import { csvToJson } from "../../utils/export";
import { useExtendedFetchData } from "../../hooks/useExtendedFetchData";

const uploadDataColumns = [
    { field: "reports", headerName: 'Date', flex: 1},
    { field: "US Bank (US)", headerName: 'US Bank (US)', flex: 1},
    { field: "Chase (US)", headerName: 'Chase (US)', flex: 1},
    { field: "Wells Fargo (US)", headerName: 'Wells Fargo (US)', flex: 1},
    { field: "Bank of America (US)", headerName: 'Bank of America (US)', flex: 1},
    { field: "Citi (US)", headerName: 'Citi (US)', flex: 1},
    { field: "PNC (US)", headerName: 'PNC (US)', flex: 1},
];

const uploadedDataColumns = [
    { field: 'upload_date', headerName: 'Upload Date', flex: 2},
    { field: 'size', headerName: 'Size', flex: 1 },
    { field: 'file_name', headerName: 'Name', flex: 2 },
    { field: 'file_type', headerName: 'Type', flex: 1 }
];

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const createRowData = (data) => {
    return data?.length > 0 ? data?.map((dataPoint, index) => {
        return {
            id: index,
            ...dataPoint,
        };
    }) : [];
};

const createFormData = (file) => {
    let formData = new FormData();
    formData.append("file", file);

    return formData;
};

export const DownDetectorUpload = () => {
    const [openAlert, setOpenAlert] = useState(false);
    const [uploadFile, setUploadFile] = useState();
    const [uploadData, setUploadData] = useState([]);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const { status: uploadedFilesStatus, data: uploadedFiles, triggerFetch: uploadedFilesTriggerFetch } = useExtendedFetchData({
        endpoint: `/downdetector/upload/files`,
        defaultData: []
    });

    const { status: uploadStatus, data: upload, triggerFetch: uploadTriggerFetch } = useExtendedFetchData({
        endpoint: `/downdetector/upload`,
        method: "POST",
        headers: {"Content-Type": "multipart/form-data"},
        onMount: false,
        defaultData: [],
        body: createFormData(uploadFile)
    });

    const { status: uploadContentStatus, content: uploadContent } = useFileContent({
        file: uploadFile
    });

    const onUploadFileChange = (e) => {
        setUploadFile(e?.target?.files?.[0]);
        setUploadModalOpen(true);
    };

    const onUploadModalClose = (e) => {
        setUploadFile(undefined);
        setUploadModalOpen(false);
    };

    const onUploadModalUpload = (e) => {
        uploadTriggerFetch();
        setUploadModalOpen(false);
    };

    const onAlertClose = (e, reason) => {
        setOpenAlert(false);
    };

    useEffect(() => {
        if(uploadContentStatus === "resolved") {
            setUploadData(csvToJson(uploadContent));
        }
    }, [uploadContentStatus, uploadContent]);

    useEffect(() => {
        if(uploadStatus === "resolved") {
            setOpenAlert(true);
            uploadedFilesTriggerFetch();
        }
    }, [uploadStatus]);

    return (
        <Grid container justifyContent="center" sx={{ px: 10, mt: 0 }} spacing={1}>
            <Grid item xs={12} md={12}>
                <Snackbar open={openAlert} autoHideDuration={6000} onClose={onAlertClose}>
                    {upload?.code !== undefined ? 
                        <Alert onClose={onAlertClose} severity="error" sx={{ width: '100%' }}>
                            {`Failed To Uploaded Data. ${upload?.code}`}
                        </Alert>
                    :
                        <Alert onClose={onAlertClose} severity="success" sx={{ width: '100%' }}>
                            Successfully Uploaded Data.
                        </Alert>
                    }
                </Snackbar>

                <Modal
                    open={uploadModalOpen}
                    onClose={onUploadModalClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: "50%",
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}>
                        <DownDetectorTable loading={uploadContentStatus === "loading"} columns={uploadDataColumns} rows={createRowData(uploadData)}/>

                        <Box sx={{display: 'flex', justifyContent: 'end', alignItems: 'center', mt: 2}}>
                            <Button variant="outlined" disabled={false} onClick={onUploadModalClose}>Cancel</Button>
                            <Button variant="contained" disabled={false} sx={{ml: 1}} onClick={onUploadModalUpload}>Upload</Button>
                        </Box>
                    </Box>
                </Modal>

                <DownDetectorItem>
                    <Box sx={{mb: 2, display: 'flex', justifyContent: 'end', alignItems: 'center'}}>
                        <Box
                            sx={{ mr: "auto", ml: 2 }}
                        >
                            <DownDetectorTitle title={"Uploaded Files"}/>
                        </Box>

                        <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}>
                            Upload file
                            <VisuallyHiddenInput type="file" accept=".csv" onChange={onUploadFileChange}/>
                        </Button>
                    </Box>

                    <DownDetectorTable loading={uploadedFilesStatus === "loading" || uploadStatus === "loading"} columns={uploadedDataColumns} rows={createRowData(uploadedFiles)}/>
                </DownDetectorItem>
            </Grid>
        </Grid>
    );
};